import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, X } from 'shared/assets/icons';
import { Colors, IconButton, Sizes } from 'shared/components/ui/icon-button';
import { useTaskListSearch } from 'shared/contexts/task-list-search';

import * as Styled from './tree-search-input.style';

export const TreeSearchInput: React.FC = () => {
  const { t } = useTranslation();
  const { searchQuery, setSearchQuery } = useTaskListSearch();
  const inputRef = useRef<HTMLInputElement>(null);
  const [isOpen, setIsOpen] = useState(false);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setSearchQuery('');
  }, [setSearchQuery]);

  const handleOpen = () => {
    setIsOpen(true);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      handleClose();
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (searchQuery) {
      return;
    }
    const wrapper = e.currentTarget.parentElement;
    if (wrapper?.contains(e.relatedTarget as Node | null)) {
      return;
    }
    handleClose();
  };

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    inputRef.current?.focus();
  }, [isOpen]);

  if (!isOpen) {
    return (
      <IconButton
        icon={Search}
        onClick={handleOpen}
        size={Sizes.Medium}
        color={Colors.Default}
      />
    );
  }

  return (
    <Styled.InputWrapper>
      <Styled.Input
        ref={inputRef}
        value={searchQuery}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        placeholder={t('general.labels.tasks.search.placeholder')}
      />
      <IconButton
        icon={X}
        onClick={handleClose}
        size={Sizes.Small}
        color={Colors.Default}
      />
    </Styled.InputWrapper>
  );
};
